import { GameService } from '@modules/game/game-service';
import Errors from '@shared/error/errors';
import { get } from 'lodash';
import moment from 'moment'
import { i18n } from '@shared/i18n';
import { SubprogramService } from '@modules/subprogram/subprogram-service';

const INITIAL_PAGE_SIZE = 10;

// const roundsOptions = (matches) => {
//   const rounds = matches.map((match) => {
//     return get(match, 'regularSeasonRound', '');
//   });
//   const roundsUniq = uniqWith(rounds, isEqual);
//   const roundOrdered = orderBy(roundsUniq, 'order', 'asc');
//   return roundOrdered;
// }

export default {
  namespaced: true,
  
  state: {
    matches: [],
    count: 0,
    filter: {},
    pagination: {},
    sorter: {},
    rowSpanPerIndex: {},
    loading: false,
    activeFilter: false,
    roundsOptions: null,
  },

  getters: {
    matches: state => state.matches,
    rowSpanPerIndex: state => state.rowSpanPerIndex,
    loading: state => state.loading,
    count: (state) => state.count,
    filter: (state) => state.filter,
    roundsOptions: (state) => state.roundsOptions,
    activeFilter: (state) => state.activeFilter,
    orderBy: (state) => {
      const sorter = state.sorter;
      if (!sorter) {
        return null;
      }
      if (!sorter.prop) {
        return null;
      }
      let direction =
        sorter.order === 'descending' ? 'DESC' : 'ASC';
      return `${sorter.prop}_${direction}`;
    },
    limit: (state) => {
      const pagination = state.pagination;
      if (!pagination || !pagination.pageSize) {
        return INITIAL_PAGE_SIZE;
      }
      return pagination.pageSize;
    },
    offset: (state) => {
      const pagination = state.pagination;
      if (!pagination || !pagination.pageSize) {
        return 0;
      }
      const currentPage = pagination.currentPage || 1;
      return (currentPage - 1) * pagination.pageSize;
    },
    pagination: (state, getters) => {
      return {
        ...state.pagination,
        total: getters.count,
        showSizeChanger: true,
      };
    },
  },

  mutations: {
    RESETED(state) {
      state.loading = false;
      state.count = 0;
      state.filter = {};
      state.pagination = {};
      state.sorter = {};
      state.roundsOptions = null;
      if (state.table) {
        state.table.clearSelection();
      }
    },

    SET_ACTIVE_FILTER(state, payload) {
      state.activeFilter = payload;
    },

    PAGINATION_CHANGED(state, payload) {
      state.pagination = payload || {};
    },

    PAGINATION_CURRENT_PAGE_CHANGED(state, payload) {
      const previousPagination = state.pagination || {};

      state.pagination = {
        currentPage: payload || 1,
        pageSize: previousPagination.pageSize || INITIAL_PAGE_SIZE,
      };
    },

    PAGINATION_PAGE_SIZE_CHANGED(state, payload) {      
      const previousPagination = state.pagination || {};

      state.pagination = {
        currentPage: previousPagination.currentPage || 1,
        pageSize: payload || INITIAL_PAGE_SIZE,
      };
    },

    SORTER_CHANGED(state, payload) {
      state.sorter = payload || {};
    },

    MATCHES_STARTED(state, payload) {
      state.rowSpanPerIndex = {};
      state.matches = [];
      state.loading = true;

      state.filter =
        payload && payload.filter ? payload.filter : {};
      state.pagination =
        payload && payload.keepPagination
          ? state.pagination
          : {};
    },
    MATCHES_SUCCESS(state, { matches, rowSpanPerIndex, count }) {
      state.matches = matches;
      state.rowSpanPerIndex = rowSpanPerIndex;
      state.loading = false;
      state.count = count;
    },
    MATCHES_ERROR(state) {
      state.rowSpanPerIndex = {};
      state.matches = [];
      state.loading = false;
      state.count = 0;
    },
    SET_ROUNDS_OPTIONS(state, payload) {
      if (!state.roundsOptions) {
        state.roundsOptions = payload || null;
      }
    },
  },

  actions: {
    setActiveFilter({ commit }, value){
      commit('SET_ACTIVE_FILTER', value)
    },

    doChangePagination({ commit, getters, dispatch },
      {subpropertyId, subprogramId, pagination}
    ) {
      commit('PAGINATION_CHANGED', pagination);
      const filter = getters.filter;
      dispatch('doFetch', {
        subpropertyId,
        subprogramId,
        filter,
        keepPagination: true,
      });
      // dispatch('doFetch', { filter, keepPagination: true });
    },

    doChangePaginationPageSize({ commit, getters, dispatch },
      {subpropertyId, programId, subprogramId, pageSize}
    ) {      
      commit('PAGINATION_PAGE_SIZE_CHANGED', pageSize);
      const filter = getters.filter;
      dispatch('doFetch', {
        subpropertyId,
        programId,
        subprogramId,
        filter,
        keepPagination: true,
      });
      // dispatch('doFetch', { filter, keepPagination: true });
    },

    doChangePaginationCurrentPage({ commit, getters, dispatch },
      {subpropertyId, programId, subprogramId, currentPage}
    ) {
      commit(
        'PAGINATION_CURRENT_PAGE_CHANGED',
        currentPage,
      );
      const filter = getters.filter;
      dispatch('doFetch', {
        subpropertyId,
        programId,
        subprogramId,
        filter,
        keepPagination: true,
      });
      // dispatch('doFetch', { filter, keepPagination: true });
    },

    doChangeSort({ commit, dispatch }, 
      {subpropertyId, programId, subprogramId, filter, sorter}) {
      commit('SORTER_CHANGED', sorter);
      // const filter = getters.filter;
      dispatch('doFetch', {
        subpropertyId,
        programId,
        subprogramId,
        filter,
        keepPagination: true,
      });
      // dispatch('doFetch', { filter, keepPagination: true });
    },

    async doFetch({commit, getters, rootGetters},
      { subpropertyId, programId, subprogramId, filter, keepPagination } = {},
      ) {
      try {
        commit('MATCHES_STARTED', { filter, keepPagination });
        const dateFormat = rootGetters['auth/manage/dateFormat'];
        const timeFormat = rootGetters['auth/manage/timeFormat'];
        
        // const { orderBy, limit, offset } = [ null, 0, 0 ];
        const filterToSave = {
          ...filter,
          subprogram: subprogramId
        }

        // Load Games
        const { rows, count } = await GameService.competitionGameList(
          subpropertyId, 
          filterToSave, 
          getters.orderBy, 
          getters.limit, 
          getters.offset, 
          );
        
        // 1.  Map data, modificar el date y hour del modelo con los datos del dateTime que recibimos
        const matches = rows.map((elem) => {     
          return {...elem, 
            timestamp: get(elem, 'timestamp', null),
            date: elem.timestamp ? moment(elem.timestamp).format(dateFormat.toUpperCase()) : i18n('common.tbd'),
            hour: elem.timestamp ? moment(elem.timestamp).format(timeFormat) : i18n('common.tbd'),
            // hour: elem.timestamp ? moment(elem.timestamp).format(timeFormat.toUpperCase()) : i18n('common.tbd'),
            awayTeam: get(elem, 'awayTeam', ''),
            homeTeam: get(elem, 'homeTeam', ''),
            location: get(elem, 'location', i18n('common.tbd')),
            status: elem.status ? elem.status : 'toPlay',
            regularSeasonRound: get(elem, 'regularSeasonRound', '')
          }
        });
      
        // 2. Order by timestamp
        // const matchesOrdered = _.orderBy(matches, ['timestamp'], ['asc']);
        
        // 3. Calcular los indices donde empieza el numero de filas que tienen la misma fecha
        const rowSpanPerIndex = {}
        let currentDate = null;
        let firstIndex = 0;

        // 4. Calculate row span per index
        matches.forEach(({ date }, index) => {
          // 1. es el mismo que el anterior
          if ( `${currentDate}` === `${date}`) {
            rowSpanPerIndex[firstIndex] = (rowSpanPerIndex[firstIndex] || 0) + 1;
          } else {
            // 2. en otro caso
            // 1. actualizamos el current index
            firstIndex = index;
            currentDate = date;
            rowSpanPerIndex[firstIndex] = (rowSpanPerIndex[firstIndex] || 0) + 1;
          }
        });

        const rounds = await SubprogramService.listRound(
          subpropertyId,
          programId,
          subprogramId
        );
        
        commit('SET_ROUNDS_OPTIONS', rounds);
        commit('MATCHES_SUCCESS', {
          matches: matches,
          count: count,
          rowSpanPerIndex
        });
      } catch (error) {
        Errors.handle(error);
        commit('MATCHES_ERROR');
      }
    },

    async doReset({ commit, dispatch }, {subpropertyId, programId, subprogramId} ) {
      commit('RESETED');
      return dispatch('doFetch', {
        subpropertyId,
        programId,
        subprogramId
      });
    },
  }
};